// src/App.tsx
// All comments are in English.

import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import AppShell from "./components/layout/AppShell";
import LoginPage from "./auth/pages/LoginPage";
import NotFound from "./components/shared/NotFound";
import ProtectedOutlet from "./components/shared/ProtectedOutlet";
import IdleTracker from "./components/shared/IdleTracker";
import { useAuth } from "./auth/hooks/useAuth";
import ProductDashboardPage from "./autodesk_usage/pages/ProductDashboardPage";
import AutodeskCustomerDetailPage from "./autodesk_usage/pages/AutodeskCustomerDetailPage";
import CorporatePage from "./autodesk_usage/pages/CorporatePage";
import SalesIntelligencePage from "./autodesk_usage/pages/SalesIntelligencePage";
import TransactionsDashboardPage from "./odoo/transactions_dashboard/pages/TransactionsDashboardPage";
import HelpdeskDashboardPage from "./odoo/helpdesk_dashboard/pages/HelpdeskDashboardPage";
import { CustomerDetailPage } from "./odoo/customers/pages/CustomerDetailPage";
import MultiCompaniesLicensesOverviewPage from "./odoo/customers/pages/MultiCompaniesLicensesOverviewPage";
import {
  canAccessAutodeskCustomer,
  canAccessHelpdeskDashboard,
  canAccessOpportunityDashboard,
  canAccessOdooCustomers,
  canAccessSalesIntelligence,
  canAccessSalesRankingInsights,
  getDefaultLandingPath,
} from "./auth/access";
import OpportunityDashboardPage from "./opportunity_dashboard/pages/OpportunityDashboardPage";


const App: React.FC = () => {
  const { user, logout } = useAuth();
  const defaultLanding = getDefaultLandingPath(user?.groups);

  const postLogin = React.useMemo(() => {
    const saved = sessionStorage.getItem("postLoginRedirect");
    return saved || defaultLanding;
  }, [defaultLanding]);

  // Use display_name first, then username, then email as last fallback
  const userName =
    user?.display_name || user?.username || user?.email || "User";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <IdleTracker />

      <Routes>
        {/* Root redirect */}
        <Route
          path="/"
          element={<Navigate to={user ? postLogin : "/login"} replace />}
        />

        {/* Login */}
        <Route
          path="/login"
          element={user ? <Navigate to={postLogin} replace /> : <LoginPage />}
        />

        {/* Protected area */}
        <Route element={<ProtectedOutlet />}>
          <Route
            element={<AppShell userName={userName} userGroups={user?.groups} onLogout={logout} />}
          >
            {/* =====================Odoo===================== */}

            {/* Opportunity Dashboard */}
            <Route
              path="/opportunity-dashboard"
              element={
                canAccessOpportunityDashboard(user?.groups) ? (
                  <OpportunityDashboardPage />
                ) : (
                  <Navigate to={defaultLanding} replace />
                )
              }
            />

            {/* Sales ranking dashboard */}
            <Route
              path="/odoo/transactions-dashboard"
              element={<Navigate to={defaultLanding} replace />}
            />
            <Route
              path="/odoo/sales-ranking-insights"
              element={
                canAccessSalesRankingInsights(user?.groups) ? (
                  <TransactionsDashboardPage />
                ) : (
                  <Navigate to={defaultLanding} replace />
                )
              }
            />
            <Route
              path="/odoo/helpdesk-dashboard"
              element={
                canAccessHelpdeskDashboard(user?.groups) ? (
                  <HelpdeskDashboardPage />
                ) : (
                  <Navigate to={defaultLanding} replace />
                )
              }
            />

            <Route
              path="/odoo/customer"
              element={
                canAccessOdooCustomers(user?.groups) ? (
                  <CustomerDetailPage />
                ) : (
                  <Navigate to={defaultLanding} replace />
                )
              }
            />
            <Route
              path="/odoo/customer/multi-licenses-overview"
              element={
                canAccessOdooCustomers(user?.groups) ? (
                  <MultiCompaniesLicensesOverviewPage />
                ) : (
                  <Navigate to={defaultLanding} replace />
                )
              }
            />
            <Route
              path="/odoo/customer/:customerId"
              element={
                canAccessOdooCustomers(user?.groups) ? (
                  <CustomerDetailPage />
                ) : (
                  <Navigate to={defaultLanding} replace />
                )
              }
            />

            {/* =================End Odoo===================== */}





            {/* ===================Autodesk=================== */}

            {/* Corporate overview */}
            <Route
              path="/autodesk-usage/corporate"
              element={
                canAccessAutodeskCustomer(user?.groups) ? (
                  <CorporatePage />
                ) : (
                  <Navigate to={defaultLanding} replace />
                )
              }
            />

            {/* Sales intelligence */}
            <Route
              path="/autodesk-usage/sales-intelligence"
              element={
                canAccessSalesIntelligence(user?.groups) ? (
                  <SalesIntelligencePage />
                ) : (
                  <Navigate to={defaultLanding} replace />
                )
              }
            />

            {/* Product dashboard */}
            <Route path="/autodesk-usage/products" element={<ProductDashboardPage />} />

            {/* Customer detail */}
            <Route path="/autodesk-usage/customers/:company_name" element={<AutodeskCustomerDetailPage />} />

            {/* ===============End Autodesk=================== */}
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};

export default App;
